import HistoryScope from '../components/HistoryScope';
import WorkbookHistoryList from '../components/WorkbookHistoryList';
import { defaultHistoryLevel, historyAccess, historyCategories, historyLevels, HistoryCategory } from '../../../shared/historyScope';
import type { UserExamRestrictions } from '../utils/api';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api, W1GrammarAnalysisResponse } from '../utils/api';
import { printHtml } from '../utils/print';
import { ArrowLeft, CheckSquare, ChevronLeft, ChevronRight, Clock, Eye, FileDown, Printer, Square, Trash2, X } from 'lucide-react';
import { isAdminRole } from '../utils/permissions';
import MathText from '../components/MathText';
import { getCscaModeDetail, getCscaModeLabel, getCscaSubjectName, getCscaTestTitle } from '../utils/cscaTestPresentation';
import { PrintablePart, PartFilter, getPrintablePart, getPrintableParts, getSectionTitle, getSectionHelp, formatWrongPrintText, resolveWrongImage } from '../utils/adminWrongQuestions';
import AdminWrongText from '../components/AdminWrongText';
import { buildW1GrammarPrintSections, W1_GRAMMAR_PRINT_CSS } from '../utils/w1GrammarPrint';

interface TestHistoryItem {
    id: string;
    date_taken: string;
    completed_at?: string | null;
    level: number;
    part: string;
    bank_version?: string;
    system_type?: 'simulation' | 'workbook' | 'past_paper' | 'topic_practice' | 'random_paper' | 'mooc_topic_practice' | 'mooc_random';
    workbook_part?: string | null;
	paper_id?: string | null;
	paper_year?: number | null;
	paper_month?: number | null;
	topic_coarse?: string | null;
	topic_fine?: string | null;
    weighted_score: number;
    max_score: number;
    review_status?: 'pending' | 'reviewed';
    reviewed_by?: string | null;
    reviewed_at?: string | null;
}

const getHistoryCompletedAt = (test: Pick<TestHistoryItem, 'completed_at' | 'date_taken'>) =>
    test.completed_at || test.date_taken;

interface AdminUserSummary {
    id?: string;
    username?: string;
}

interface HistoryLocationState {
    returnTo?: string;
    user?: AdminUserSummary;
    updatedReview?: {
        id: string;
        review_status: 'pending' | 'reviewed';
        reviewed_by?: string | null;
        reviewed_at?: string | null;
    };
}

const getNormalizedReviewStatus = (
    item: Pick<TestHistoryItem, 'review_status' | 'reviewed_at'>
): 'pending' | 'reviewed' =>
    item.review_status === 'reviewed' || !!item.reviewed_at ? 'reviewed' : 'pending';

const mergeUpdatedReview = (
    items: TestHistoryItem[],
    updatedReview?: HistoryLocationState['updatedReview']
): TestHistoryItem[] => {
    if (!updatedReview?.id) return items;
    return items.map((test) => (
        test.id === updatedReview.id
            ? {
                ...test,
                review_status: updatedReview.review_status === 'reviewed' || !!updatedReview.reviewed_at ? 'reviewed' : 'pending',
                reviewed_by: updatedReview.reviewed_by ?? null,
                reviewed_at: updatedReview.reviewed_at ?? null,
            }
            : test
    ));
};

interface WrongQuestionItem {
    id: string;
    question_id: string;
    part: string;
    printablePart: PrintablePart;
    question_text: string;
    options: string[];
    correct_option_index: number | null;
    chosen_option_index: number | null;
    answer_content: string[] | string | null;
    chose_buzhidao: boolean;
    explanation?: string;
    media?: string | null;
}



const getDeleteLabel = (part: string) => {
    const normalized = String(part || '').toLowerCase();
    if (normalized.includes('simulation')) return 'Simulation';
    if (normalized.startsWith('l') || normalized.includes('listening')) return 'Listening';
    if (normalized.startsWith('r') || normalized.includes('reading')) return 'Reading';
    if (normalized.startsWith('w') || normalized.includes('writing')) return 'Writing';
    return 'Test';
};

const getAnswerLines = (count: number) => Array.from({ length: count }, (_, index) => `line-${index}`);

const escapeHtml = (value: string) =>
    String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const formatQuestionTextForPrint = formatWrongPrintText;
const getPartHelpText = getSectionHelp;

const resolveMediaUrl = (media?: string | null) => {
    if (!media) return '';
    if (media.startsWith('http://') || media.startsWith('https://')) return media;
    const base = BACKEND_URL.replace(/\/$/, '');
    return `${base}/media${media.startsWith('/') ? '' : '/'}${media}`;
};

const buildPrintableHtml = ({
    userName,
    test,
    level,
    items,
    grammarSections = ''
}: {
    userName: string;
    test: TestHistoryItem;
    level: number;
    items: WrongQuestionItem[];
    grammarSections?: string;
}) => {
    const plainItems = grammarSections ? items.filter(item => item.printablePart !== 'W1') : items;
    const grouped = getPrintableParts(plainItems)
        .map((part) => ({
            part,
            items: plainItems.filter((item) => item.printablePart === part)
        }))
        .filter((group) => group.items.length > 0);

    const sections = grouped.map((group, sectionIndex) => {
        const questionsHtml = group.items.map((item, index) => {
            const questionNumber = index + 1;
            const mediaUrl = resolveMediaUrl(item.media);

            const optionsHtml = item.printablePart !== 'W1' && item.options.length > 0
                ? `
                    <div class="options">
                        ${item.options.map((option, optionIndex) => `
                            <div class="option-row">
                                <span class="option-label">${String.fromCharCode(65 + optionIndex)}.</span>
                                <span class="option-text">${formatQuestionTextForPrint(option)}</span>
                            </div>
                        `).join('')}
                    </div>
                `
                : '';

            const mediaHtml = resolveWrongImage(item.media) ? formatQuestionTextForPrint(item.media || '') : mediaUrl
                ? `
                    <div class="media-note">
                        Media: <a href="${escapeHtml(mediaUrl)}">${escapeHtml(mediaUrl)}</a>
                    </div>
                `
                : '';

            const answerAreaHtml = group.part.startsWith('R') || group.part === 'MATH'
                ? `
                    <div class="answer-box">
                        <span class="answer-label">Answer:</span>
                        <span class="choice-circle"></span>
                        <span class="choice-circle"></span>
                        <span class="choice-circle"></span>
                        <span class="choice-circle"></span>
                    </div>
                `
                : group.part === 'W1'
                    ? `
                        <div class="answer-area">
                            <div class="answer-label">Answer:</div>
                            ${getAnswerLines(5).map(() => '<div class="answer-line"></div>').join('')}
                        </div>
                    `
                    : `
                        <div class="answer-area">
                            <div class="answer-label">Answer:</div>
                            ${getAnswerLines(3).map(() => '<div class="answer-line"></div>').join('')}
                        </div>
                    `;

            return `
                <article class="question-card">
                    <div class="question-number">${questionNumber}.</div>
                    <div class="question-body">
                        <div class="question-text">${formatQuestionTextForPrint(item.question_text)}</div>
                        ${mediaHtml}
                        ${optionsHtml}
                        ${answerAreaHtml}
                    </div>
                </article>
            `;
        }).join('');

        return `
            <section class="print-section ${sectionIndex > 0 ? 'page-break' : ''}">
                <header class="section-header">
                    <div class="section-title">${escapeHtml(getSectionTitle(group.part))}</div>
                    <div class="section-subtitle">${escapeHtml(getPartHelpText(group.part))}</div>
                </header>
                ${questionsHtml}
            </section>
        `;
    }).join('');

    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8" />
            <title>Wrong Questions Print</title>
            <style>
                ${W1_GRAMMAR_PRINT_CSS}
                * { box-sizing: border-box; }
                body {
                    font-family: Arial, Helvetica, sans-serif;
                    color: #111827;
                    margin: 0;
                    padding: 28px;
                    background: #ffffff;
                    line-height: 1.5;
                }
                .sheet-header {
                    border-bottom: 2px solid #d1d5db;
                    padding-bottom: 16px;
                    margin-bottom: 24px;
                }
                .sheet-title {
                    font-size: 24px;
                    font-weight: 700;
                    margin-bottom: 10px;
                }
                .meta-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 8px 20px;
                    font-size: 14px;
                }
                .meta-label {
                    color: #6b7280;
                    display: inline-block;
                    min-width: 72px;
                }
                .print-section {
                    margin-top: 12px;
                }
                .page-break {
                    page-break-before: always;
                    break-before: page;
                }
                .section-header {
                    margin-bottom: 18px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .section-title {
                    font-size: 20px;
                    font-weight: 700;
                }
                .section-subtitle {
                    font-size: 13px;
                    color: #6b7280;
                    margin-top: 4px;
                }
                .question-card {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 20px;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                .question-number {
                    font-weight: 700;
                    font-size: 18px;
                    min-width: 28px;
                }
                .question-body {
                    flex: 1;
                }
                .question-text {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 10px;
                    white-space: normal;
                }
                .media-note {
                    font-size: 12px;
                    color: #4b5563;
                    margin-bottom: 8px;
                    word-break: break-all;
                }
                .options {
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 12px;
                    background: #f9fafb;
                    margin-bottom: 12px;
                }
                .option-row {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 8px;
                }
                .option-row:last-child {
                    margin-bottom: 0;
                }
                .option-label {
                    width: 20px;
                    font-weight: 700;
                }
                .option-text {
                    flex: 1;
                }
                .answer-box {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-top: 10px;
                    min-height: 24px;
                }
                .answer-area {
                    margin-top: 12px;
                }
                .answer-label {
                    font-size: 13px;
                    color: #374151;
                    margin-bottom: 6px;
                    font-weight: 600;
                }
                .choice-circle {
                    width: 18px;
                    height: 18px;
                    border: 1.5px solid #6b7280;
                    border-radius: 9999px;
                    display: inline-block;
                }
                .answer-line {
                    border-bottom: 1px solid #9ca3af;
                    height: 28px;
                    margin-bottom: 10px;
                }
                @page {
                    size: A4;
                    margin: 14mm;
                }
            </style>
        </head>
        <body>
            <header class="sheet-header">
                <div class="sheet-title">Wrong Questions</div>
                <div class="meta-grid">
                    <div><span class="meta-label">Name</span>${escapeHtml(userName)}</div>
                    <div><span class="meta-label">Exam</span>${test.bank_version === 'CSCA' ? escapeHtml(getCscaTestTitle(test)) : `HSK ${escapeHtml(String(level))}`}</div>
                    <div><span class="meta-label">Completed</span>${escapeHtml(new Date(getHistoryCompletedAt(test)).toLocaleString())}</div>
                    <div><span class="meta-label">Test Type</span>${test.bank_version === 'CSCA' ? escapeHtml(getCscaModeDetail(test) || getCscaModeLabel(test)) : escapeHtml(test.part)}</div>
                </div>
            </header>
            ${grammarSections}
            ${sections}
        </body>
        </html>
    `;
};

const AdminStudentHistory: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { token, user: currentUser } = useAuthStore();
    const canDeleteTests = isAdminRole(currentUser?.role);
    const locationState = (location.state || {}) as HistoryLocationState;
    const user = (locationState.user || {}) as AdminUserSummary;

    const [history, setHistory] = useState<TestHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingTestId, setDeletingTestId] = useState<string | null>(null);
    const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
    const [selectedBankVersion, setSelectedBankVersion] = useState<string>('');
    const [restrictions, setRestrictions] = useState<UserExamRestrictions | null>(null);
    const [workbookSource, setWorkbookSource] = useState('chapters');
    const [category, setCategory] = useState<HistoryCategory>('simulation');
    const [wrongQuestionTest, setWrongQuestionTest] = useState<TestHistoryItem | null>(null);
    const [wrongQuestionsLoading, setWrongQuestionsLoading] = useState(false);
    const [grammarPrinting, setGrammarPrinting] = useState(false);
    const grammarPrintCache = useRef(new Map<string, W1GrammarAnalysisResponse>());
    const [wrongQuestionsError, setWrongQuestionsError] = useState('');
    const [wrongQuestions, setWrongQuestions] = useState<WrongQuestionItem[]>([]);
    const [partFilter, setPartFilter] = useState<PartFilter>('ALL');
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 5;

    const requestSequence = useRef(0);
    const loadHistory = async () => {
        const sequence = ++requestSequence.current;
        if (!token || !id || !selectedBankVersion || !restrictions) return;
        if (selectedBankVersion === '3.0' && category === 'practice' && workbookSource === 'chapters') { setLoading(false); return; }
        setLoading(true);
        setError('');
        try {
            const res = await api.getAdminUserHistory(id, token, {
                page,
                limit: pageSize,
                bankVersion: selectedBankVersion,
                level: selectedLevel,
                category,
            });
            if (sequence !== requestSequence.current) return;
            if (res.success) {
                const normalized = res.history.map((item) => ({
                    ...item,
                    review_status: getNormalizedReviewStatus(item),
                }));
                const nextHistory = mergeUpdatedReview(normalized, locationState.updatedReview);
                nextHistory.sort((a, b) =>
                    new Date(getHistoryCompletedAt(b)).getTime() - new Date(getHistoryCompletedAt(a)).getTime()
                );
                setHistory(nextHistory);
                setTotal(res.pagination?.total || 0);
                setTotalPages(res.pagination?.total_pages || 1);
            }
        } catch (err: any) {
            if (sequence !== requestSequence.current) return;
            setHistory([]);
            console.error('Failed to load history', err);
            setError(err?.message || 'Failed to load history');
        } finally {
            if (sequence === requestSequence.current) setLoading(false);
        }
    };

    useEffect(() => {
      if (!token || !id) return;
      let active = true;
      api.getStudentRestrictions(id, token).then(data => {
        if (!active) return;
        setRestrictions(data);
        const params = new URLSearchParams(location.search), access = historyAccess(data);
        const bank = access.families.includes(params.get('bankVersion') as any) ? params.get('bankVersion')! : access.families[0] || '';
        setSelectedBankVersion(bank);
        setWorkbookSource(params.get('source') === 'legacy' ? 'legacy' : 'chapters');
        setCategory(historyCategories(bank).includes(params.get('category') as any) ? params.get('category') as HistoryCategory : historyCategories(bank)[0]);
        setSelectedLevel(params.get('level') === 'all' ? null : historyLevels(data, bank).includes(Number(params.get('level'))) ? Number(params.get('level')) : defaultHistoryLevel(data, bank) === 'all' ? null : Number(defaultHistoryLevel(data, bank))); setPage(Math.max(1, Number(params.get('page')) || 1));
        if (!bank) setLoading(false);
      }).catch(e => { if (active) { setError(e.message); setLoading(false); } });
      return () => { active = false; };
    }, [id, token, location.search]);

    useEffect(() => {
        if (!token || !id) return;
        loadHistory();
        return () => { requestSequence.current++; };
    }, [id, token, page, selectedBankVersion, selectedLevel, category, restrictions, workbookSource]);

    useEffect(() => {
        const updatedReview = locationState.updatedReview;
        if (!updatedReview?.id) return;
        setHistory((prev) => mergeUpdatedReview(prev, updatedReview));
    }, [locationState.updatedReview]);

    const handleDeleteTest = async (test: TestHistoryItem) => {
        if (!token || !id) return;
        const label = getDeleteLabel(test.part);
        const confirmed = window.confirm(`Delete this ${label.toLowerCase()} test from ${new Date(getHistoryCompletedAt(test)).toLocaleString()} and restore the proficiency changes caused by it?`);
        if (!confirmed) return;

        setDeletingTestId(test.id);
        setError('');
        try {
            const res = await api.deleteAdminUserTest(id, test.id, token);
            if (res.success) {
                await loadHistory();
            }
        } catch (err: any) {
            console.error('Failed to delete test', err);
            setError(err?.message || 'Failed to delete test');
        } finally {
            setDeletingTestId(null);
        }
    };

    const closeWrongQuestionsModal = () => {
        setWrongQuestionTest(null);
        setWrongQuestions([]);
        setWrongQuestionsError('');
        setWrongQuestionsLoading(false);
        setPartFilter('ALL');
        setSelectedQuestionIds([]);
    };

    const openWrongQuestions = async (test: TestHistoryItem) => {
        if (!token) return;

        setWrongQuestionTest(test);
        setWrongQuestionsLoading(true);
        setWrongQuestionsError('');
        setWrongQuestions([]);
        setPartFilter('ALL');
        setSelectedQuestionIds([]);

        try {
            const data = await api.getAdminTestDetail(test.id, token);
            const filtered = (data.items || [])
                .filter((item: any) => item.correct === false || item.chose_buzhidao === true)
                .map((item: any, index: number) => {
                    const printablePart = getPrintablePart(item.part, test.bank_version);
                    if (!printablePart) return null;

                    return {
                        id: String(item.id || `${item.question_id}-${index}`),
                        question_id: String(item.question_id || item.id || index),
                        part: String(item.part || ''),
                        printablePart,
                        question_text: String(item.question_text || ''),
                        options: Array.isArray(item.options) ? item.options.map((option: any) => String(option ?? '')) : [],
                        correct_option_index: Number.isInteger(item.correct_option_index) ? item.correct_option_index : null,
                        chosen_option_index: Number.isInteger(item.chosen_option_index) ? item.chosen_option_index : null,
                        answer_content: Array.isArray(item.answer_content)
                            ? item.answer_content.map((entry: any) => String(entry ?? ''))
							: item.answer_content && typeof item.answer_content === 'object' && Array.isArray(item.answer_content.sequence)
								? item.answer_content.sequence.map((entry: any) => String(entry ?? ''))
								: item.answer_content && typeof item.answer_content === 'object' && typeof item.answer_content.text === 'string'
									? item.answer_content.text
                            : typeof item.answer_content === 'string'
                                ? item.answer_content
                                : null,
                        chose_buzhidao: !!item.chose_buzhidao,
                        explanation: item.explanation ? String(item.explanation) : '',
                        media: item.media ? String(item.media) : null
                    } as WrongQuestionItem;
                })
                .filter(Boolean) as WrongQuestionItem[];

            setWrongQuestions(filtered);
            setSelectedQuestionIds(filtered.map((item) => item.id));
        } catch (err: any) {
            console.error('Failed to load wrong questions', err);
            setWrongQuestionsError(err?.message || 'Failed to load wrong questions');
        } finally {
            setWrongQuestionsLoading(false);
        }
    };

    const filteredHistory = history;
    const isCscaWrongModal = wrongQuestionTest?.bank_version === 'CSCA';
    const partFilterOptions: PartFilter[] = ['ALL', ...getPrintableParts(wrongQuestions)];

    const visibleWrongQuestions = useMemo(() => {
        if (partFilter === 'ALL') return wrongQuestions;
        return wrongQuestions.filter((item) => item.printablePart === partFilter);
    }, [partFilter, wrongQuestions]);

    const selectedWrongQuestions = useMemo(
        () => wrongQuestions.filter((item) => selectedQuestionIds.includes(item.id)),
        [wrongQuestions, selectedQuestionIds]
    );

    const visibleSelectedCount = visibleWrongQuestions.filter((item) => selectedQuestionIds.includes(item.id)).length;

    const toggleQuestionSelection = (questionId: string) => {
        setSelectedQuestionIds((prev) =>
            prev.includes(questionId) ? prev.filter((idValue) => idValue !== questionId) : [...prev, questionId]
        );
    };

    const selectAllVisible = () => {
        setSelectedQuestionIds((prev) => {
            const next = new Set(prev);
            visibleWrongQuestions.forEach((item) => next.add(item.id));
            return Array.from(next);
        });
    };

    const clearVisibleSelection = () => {
        const visibleIds = new Set(visibleWrongQuestions.map((item) => item.id));
        setSelectedQuestionIds((prev) => prev.filter((questionId) => !visibleIds.has(questionId)));
    };

    const downloadSelectedAsJson = () => {
        if (!wrongQuestionTest || selectedWrongQuestions.length === 0) return;

        const payload = {
            version: '1.0',
            exported_at: new Date().toISOString(),
            source: 'admin-test-wrong-questions',
            user: {
                id: id,
                username: user?.username || `User ${id}`
            },
            test: {
                id: wrongQuestionTest.id,
                date_taken: wrongQuestionTest.date_taken,
                level: wrongQuestionTest.level,
                part: wrongQuestionTest.part
            },
            selected_types: Array.from(new Set(selectedWrongQuestions.map((item) => item.printablePart))),
            items: selectedWrongQuestions.map((item) => ({
                question_id: item.question_id,
                part: item.part,
                printable_part: item.printablePart,
                question_text: item.question_text,
                options: item.options,
                media: item.media,
                student_answer: {
                    chosen_option_index: item.chosen_option_index,
                    answer_content: item.answer_content,
                    chose_buzhidao: item.chose_buzhidao
                },
                correct_answer: {
                    correct_option_index: item.correct_option_index
                },
                explanation: item.explanation || ''
            }))
        };

        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const safeName = (user?.username || `user-${id}`).replace(/[^\w-]+/g, '_');
        anchor.href = url;
        anchor.download = `${safeName}-${wrongQuestionTest.id}-wrong-questions.json`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    const printSelectedQuestions = async () => {
        if (!wrongQuestionTest || selectedWrongQuestions.length === 0 || grammarPrinting || !id || !token) return;
        setGrammarPrinting(true);
        setWrongQuestionsError('');
        try {
            const w1 = selectedWrongQuestions.filter(item => item.printablePart === 'W1');
            const bankVersion = wrongQuestionTest.bank_version || '2.0';
            let grammarSections = '';
            if (bankVersion === '2.0' && [3, 4].includes(Number(wrongQuestionTest.level)) && w1.length) {
                const key = `${id}:${bankVersion}:${wrongQuestionTest.level}`;
                const cached = grammarPrintCache.current.get(key);
                const ids = new Set(cached?.categories.flatMap(category => category.questions.map(question => question.question_id)));
                const analysis = cached && w1.every(question => ids.has(question.question_id)) ? cached : await api.analyzeAdminUserW1Grammar(id, token, bankVersion, wrongQuestionTest.level);
                grammarPrintCache.current.set(key, analysis);
                grammarSections = buildW1GrammarPrintSections(analysis, w1);
            }
            const html = buildPrintableHtml({ userName: user?.username || `User ${id}`, test: wrongQuestionTest, level: wrongQuestionTest.level, items: selectedWrongQuestions, grammarSections });
            printHtml(html, 'Unable to open print preview.');
        } catch (printError: any) {
            setWrongQuestionsError(printError?.message || 'Failed to prepare the grammar PDF.');
        } finally {
            setGrammarPrinting(false);
        }
    };

    const openAllWrongPage = () => {
        if (!selectedLevel || selectedBankVersion === 'all') {
            window.alert('Please choose one exam type and one level first.');
            return;
        }
        navigate(`/admin/users/${id}/wrong-history?level=${selectedLevel}&bankVersion=${selectedBankVersion}&category=${category}`, { state: { user, returnTo: `${location.pathname}?bankVersion=${selectedBankVersion}&category=${category}&level=${selectedLevel}&page=${page}&source=${workbookSource}` } });
    };

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6 flex items-center justify-between">
                <div className="flex items-center">
                    <button
                        onClick={() => navigate(`/admin/users/${id}`, { state: { user, returnTo: locationState.returnTo } })}
                        className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-600"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                             Test History
                        </h1>
                        <p className="text-gray-500 text-sm">
                            {user?.username ? `For ${user.username}` : `User ID: ${id}`}
                        </p>
                    </div>
                </div>
            </div>

            {restrictions && <HistoryScope restrictions={restrictions} family={selectedBankVersion} category={category} level={selectedLevel == null ? 'all' : String(selectedLevel)} onChange={(family, cat, level) => { setWorkbookSource('chapters'); setSelectedBankVersion(family); setCategory(cat); setSelectedLevel(level === 'all' ? null : Number(level)); setPage(1); }} />}
            {!selectedBankVersion && error && <p role="alert" className="rounded-lg bg-red-50 p-4 text-red-700">{error}</p>}
            {selectedBankVersion === '3.0' && category === 'practice' && <div className="flex gap-3">{[['chapters','章节习题'],['legacy','早期习题记录']].map(([value,label]) => <button key={value} aria-pressed={workbookSource === value} onClick={() => { setWorkbookSource(value); setPage(1); }} className={`rounded-lg border px-4 py-2 ${workbookSource === value ? 'bg-indigo-50 text-indigo-700' : 'bg-white'}`}>{label}</button>)}</div>}
            {selectedBankVersion === '3.0' && category === 'practice' && workbookSource === 'chapters' && token && id ? <WorkbookHistoryList token={token} level={selectedLevel == null ? 'all' : String(selectedLevel)} studentId={id} /> : selectedBankVersion ? <>
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-gray-500" />
                        Recent Completed Tests
                    </h2>

                    <div className="flex items-center space-x-2">
                         <button
                            onClick={openAllWrongPage}
                            disabled={!selectedLevel || selectedBankVersion === 'all'}
                            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                         >
                            <Eye className="mr-2 h-4 w-4" />
                            View All Wrong
                         </button>
                    </div>
                </div>

                {error && (
                    <div className="mx-6 mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {loading && !history.length ? <div className="min-h-[200px]" aria-busy="true" /> : filteredHistory.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        No test history found matching the criteria.
                    </div>
                ) : (
                    <div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredHistory.map((test) => {
                                    const deleteLabel = getDeleteLabel(test.part);
                                    const isDeleting = deletingTestId === test.id;
                                    const normalizedReviewStatus = getNormalizedReviewStatus(test);
                                    return (
                                        <tr key={test.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(getHistoryCompletedAt(test)).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {test.bank_version === 'CSCA' ? 'CSCA' : `HSK ${test.bank_version || '2.0'}`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {test.bank_version === 'CSCA' ? getCscaSubjectName(test.level) : `HSK ${test.level}`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${test.system_type === 'workbook' ? 'bg-indigo-100 text-indigo-700' : test.system_type === 'past_paper' ? 'bg-emerald-100 text-emerald-700' : test.system_type === 'topic_practice' ? 'bg-violet-100 text-violet-700' : test.system_type === 'random_paper' ? 'bg-fuchsia-100 text-fuchsia-700' : test.system_type?.startsWith('mooc_') ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {test.bank_version === 'CSCA' ? getCscaModeLabel(test) : test.system_type === 'workbook' ? 'Workbook' : 'Simulation'}
                                                </span>
                                                {test.system_type === 'workbook' && test.workbook_part && (
                                                    <div className="text-xs text-gray-400 mt-1">{test.workbook_part}</div>
                                                )}
                                                {test.bank_version === 'CSCA' && getCscaModeDetail(test) && (
                                                    <div className="text-xs text-gray-400 mt-1">{getCscaModeDetail(test)}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                                {test.part}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {test.weighted_score.toFixed(1)} <span className="text-gray-400 font-normal">/ {test.max_score}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                        normalizedReviewStatus === 'reviewed'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-amber-100 text-amber-800'
                                                    }`}>
                                                        {normalizedReviewStatus === 'reviewed' ? 'Reviewed' : 'Pending'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex items-center gap-4">
                                                    <button
                                                        onClick={() => navigate(`/admin/test/${test.id}`, {
                                                            state: {
                                                                returnTo: `${location.pathname}?bankVersion=${selectedBankVersion}&category=${category}&level=${selectedLevel || ''}&page=${page}&source=${workbookSource}`,
                                                                user,
                                                                testSummary: {
                                                                    id: test.id,
                                                                    userId: id,
                                                                    level: test.level,
                                                                    part: test.part,
                                                                    weightedScore: test.weighted_score,
                                                                    maxScore: test.max_score,
                                                                    completedAt: getHistoryCompletedAt(test),
                                                                    reviewStatus: normalizedReviewStatus,
                                                                    reviewedBy: test.reviewed_by ?? null,
                                                                    reviewedAt: test.reviewed_at ?? null,
                                                                }
                                                            }
                                                        })}
                                                        className="text-blue-600 hover:text-blue-900"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => openWrongQuestions(test)}
                                                        className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                                                    >
                                                        <Eye className="mr-1 h-4 w-4" />
                                                        View Wrong
                                                    </button>
                                                    {canDeleteTests && (
                                                      <button
                                                          onClick={() => handleDeleteTest(test)}
                                                          disabled={isDeleting}
                                                          className={`inline-flex items-center text-red-600 hover:text-red-800 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                      >
                                                          <Trash2 className="mr-1 h-4 w-4" />
                                                          {isDeleting ? 'Deleting...' : `Delete ${deleteLabel}`}
                                                      </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                      </div>
                      <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
                        <span>
                          Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total} tests
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            disabled={page <= 1}
                            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                          </button>
                          <span>Page {page} of {totalPages}</span>
                          <button
                            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                            disabled={page >= totalPages}
                            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Next <ChevronRight className="ml-1 h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                )}
            </div>

            </> : null}
            {wrongQuestionTest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
                    <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <h3 className="text-xl font-semibold text-slate-900">Wrong Questions</h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    {user?.username ? `${user.username} · ` : ''}
                                    {new Date(wrongQuestionTest.date_taken).toLocaleString()} · {isCscaWrongModal ? getCscaTestTitle(wrongQuestionTest) : `HSK ${wrongQuestionTest.level} · ${wrongQuestionTest.part}`}
                                </p>
                                <p className="mt-2 text-xs text-slate-500">
                                    {isCscaWrongModal ? 'CSCA 数学错题、答案和解析。' : 'All reading and writing wrong questions are shown. Listening is excluded.'}
                                </p>
                            </div>
                            <button
                                onClick={closeWrongQuestionsModal}
                                className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="border-b border-slate-200 px-6 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                                {partFilterOptions.map((option) => {
                                    const active = partFilter === option;
                                    return (
                                        <button
                                            key={option}
                                            onClick={() => setPartFilter(option)}
                                            className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                                active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {option === 'ALL' ? 'All' : option}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                                    <span>{visibleWrongQuestions.length} visible</span>
                                    <span className="text-slate-300">|</span>
                                    <span>{selectedWrongQuestions.length} selected</span>
                                    <span className="text-slate-300">|</span>
                                    <span>{visibleSelectedCount} selected in current filter</span>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        onClick={selectAllVisible}
                                        disabled={visibleWrongQuestions.length === 0}
                                        className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <CheckSquare className="mr-2 h-4 w-4" />
                                        Select Visible
                                    </button>
                                    <button
                                        onClick={clearVisibleSelection}
                                        disabled={visibleSelectedCount === 0}
                                        className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Square className="mr-2 h-4 w-4" />
                                        Clear Visible
                                    </button>
                                    <button
                                        onClick={downloadSelectedAsJson}
                                        disabled={selectedWrongQuestions.length === 0}
                                        className="inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FileDown className="mr-2 h-4 w-4" />
                                        Export JSON
                                    </button>
                                    <button
                                        onClick={() => void printSelectedQuestions()}
                                        disabled={selectedWrongQuestions.length === 0 || grammarPrinting}
                                        className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Printer className="mr-2 h-4 w-4" />
                                        {grammarPrinting ? 'Preparing PDF...' : 'Print PDF'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="min-h-[240px] flex-1 overflow-y-auto px-6 py-5">
                            {wrongQuestionsLoading ? (
                                <div className="flex h-full items-center justify-center">
                                    <div className="text-center">
                                        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                                        <p className="mt-3 text-sm text-slate-500">Loading wrong questions...</p>
                                    </div>
                                </div>
                            ) : wrongQuestionsError ? (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    {wrongQuestionsError}
                                </div>
                            ) : visibleWrongQuestions.length === 0 ? (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 px-6 py-10 text-center text-slate-500">
                                    No printable wrong questions found for this test.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {visibleWrongQuestions.map((item, index) => {
                                        const isSelected = selectedQuestionIds.includes(item.id);
                                        const mediaUrl = resolveMediaUrl(item.media);
                                        const isMultipleChoice = item.printablePart.startsWith('R') || item.printablePart === 'MATH';
                                        const isSequence = item.printablePart === 'W1';
                                        const isFillBlank = item.printablePart === 'W2';

                                        return (
                                            <div
                                                key={item.id}
                                                className={`rounded-2xl border p-5 shadow-sm transition ${
                                                    isSelected ? 'border-indigo-300 bg-indigo-50/40' : 'border-slate-200 bg-white'
                                                }`}
                                            >
                                                <div className="flex items-start gap-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleQuestionSelection(item.id)}
                                                        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                                                            isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-transparent'
                                                        }`}
                                                        aria-label={isSelected ? 'Deselect question' : 'Select question'}
                                                    >
                                                        <CheckSquare className="h-4 w-4" />
                                                    </button>

                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                                                Question {index + 1}
                                                            </span>
                                                            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                                                {item.printablePart}
                                                            </span>
                                                            <span className="text-xs text-slate-400">{item.question_id}</span>
                                                        </div>

                                                        {isCscaWrongModal ? (
                                                            <MathText content={item.question_text} className="mt-3 block text-base font-medium leading-7 text-slate-900" />
                                                        ) : (
                                                            <div className="mt-3 whitespace-pre-line text-base font-medium leading-7 text-slate-900">
                                                                <AdminWrongText text={item.question_text} />
                                                            </div>
                                                        )}

                                                        {mediaUrl && (
                                                            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 break-all">
                                                                {resolveWrongImage(item.media) ? <AdminWrongText text={item.media || ''} /> : <>Media: {mediaUrl}</>}
                                                            </div>
                                                        )}

                                                        {item.options.length > 0 && (
                                                            <div className="mt-4 space-y-2">
                                                                {item.options.map((option, optionIndex) => (
                                                                    <div key={`${item.id}-option-${optionIndex}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                                                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-500">
                                                                            {String.fromCharCode(65 + optionIndex)}
                                                                        </span>
                                                                        {isCscaWrongModal ? <MathText content={option} /> : <AdminWrongText text={option} />}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {isMultipleChoice && (
                                                            <div className="mt-4 text-sm text-slate-500">
                                                                {isCscaWrongModal
                                                                    ? `Student answer: ${item.chosen_option_index == null ? 'Unanswered' : String.fromCharCode(65 + item.chosen_option_index)} · Correct answer: ${item.correct_option_index == null ? '-' : String.fromCharCode(65 + item.correct_option_index)}`
                                                                    : 'Reading question. PDF uses a unified multiple-choice layout.'}
                                                            </div>
                                                        )}

                                                        {isCscaWrongModal && item.explanation && (
                                                            <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                                                                <span className="mb-1 block font-semibold">Explanation</span>
                                                                <MathText content={item.explanation} />
                                                            </div>
                                                        )}

                                                        {isSequence && (
                                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                                                                W1 export keeps blank answer lines for sentence ordering.
                                                            </div>
                                                        )}

                                                        {isFillBlank && (
                                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                                                                W2 export keeps blank answer lines for fill-in-the-blank writing.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminStudentHistory;
